import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { call, put, takeEvery } from "redux-saga/effects";
import { City } from "../../interfaces";
import { Paginated } from "../../interfaces/Paginated";
import { googleFunctionService } from "../../services/axios";
import * as sagaActions from "./sagaActions";
import { actions } from "./slice";
import * as CityActionTypes from "./types";

export function* getAllCitySaga({
  payload,
}: ReturnType<typeof sagaActions.loadAllCity>) {
  yield put(actions.setLoader({ fetchableProp: "allCities", isLoading: true }));

  try {
    const {
      data,
    }: AxiosResponse<Paginated<City>> = yield call(
      googleFunctionService.get,
      "/city?limit=1000",
      { params: payload }
    );
    yield put(actions.setPaginatedCities(data));
  } catch (err) {
    actions.setError({ fetchableProp: "allCities", error: err });
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "allCities", isLoading: false })
    );
  }
}

export function* createCitySaga({ payload }: PayloadAction<Omit<City, "id">>) {
  yield put(
    actions.setLoader({ fetchableProp: "createdCity", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<City> = yield call(
      googleFunctionService.post,
      "/city",
      payload
    );
    yield put(actions.createCity(data));
  } catch (err) {
    yield put(actions.setError({ fetchableProp: "createdCity", error: err }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "createdCity", isLoading: false })
    );
  }
}

export function* updateCitySaga({
  payload,
}: ReturnType<typeof sagaActions.updateCity>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedCity", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<City> = yield call(
      googleFunctionService,
      `/city/${payload.id}`,
      {
        method: "PUT",
        data: payload,
      }
    );
    yield put(actions.updateCity(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "updatedCity", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedCity", isLoading: false })
    );
  }
}

export function* deleteCitySaga({
  payload,
}: ReturnType<typeof sagaActions.deleteCityById>) {
  yield put(
    actions.setLoader({ fetchableProp: "deletedCity", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<City> = yield call(
      googleFunctionService.delete,
      `/city/${payload}`
    );
    yield put(actions.deleteCity(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "deletedCity", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "deletedCity", isLoading: false })
    );
  }
}

export function* getCityByIdSaga({
  payload,
}: ReturnType<typeof sagaActions.loadCityById>) {
  yield put(actions.setLoader({ fetchableProp: "city", isLoading: true }));

  try {
    const { data }: AxiosResponse<City> = yield call(
      googleFunctionService.get,
      `/city/${payload}`
    );
    yield put(actions.setCity(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "city", error }));
  } finally {
    yield put(actions.setLoader({ fetchableProp: "city", isLoading: false }));
  }
}

export function* createCitiesSaga({
  payload,
}: ReturnType<typeof sagaActions.createCities>) {
  yield put(
    actions.setLoader({ fetchableProp: "createdCity", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<City[]> = yield call(
      googleFunctionService.post,
      "/city",
      {
        data: payload,
      }
    );
    yield put(actions.createCity(data));
  } catch (err) {
    yield put(actions.setError({ fetchableProp: "createdCity", error: err }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "createdCity", isLoading: false })
    );
  }
}

export default function* rootSaga() {
  yield takeEvery(CityActionTypes.fetch_paginated_cities_saga, getAllCitySaga);
  yield takeEvery(CityActionTypes.fetch_city_by_id_saga, getCityByIdSaga);
  yield takeEvery(CityActionTypes.create_new_city_saga, createCitySaga);
  yield takeEvery(CityActionTypes.create_cities_saga, createCitiesSaga);
  yield takeEvery(CityActionTypes.delete_city_saga, deleteCitySaga);
  yield takeEvery(CityActionTypes.update_city_saga, updateCitySaga);
}
